import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    loadProduct();
  }, [slug]);

  const loadProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/products/slug/${slug}`);
      setProduct(response.data);

      if (response.data.category_id) {
        const categoryRes = await axios.get(`${API_URL}/categories/${response.data.category_id}`);
        setCategory(categoryRes.data);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Produto não encontrado');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${quantity} ${quantity > 1 ? 'itens adicionados' : 'item adicionado'} ao carrinho!`);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/carrinho');
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#273e82]"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const hidePrice = !!product.omit_price;
  const hasDiscount =
    !hidePrice &&
    product.promo_price &&
    product.price != null &&
    product.promo_price < product.price;

  const displayPrice = hasDiscount ? product.promo_price : product.price;

  const images =
    product.images && product.images.length > 0
      ? product.images.map(img => img.image_url)
      : ['https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=800'];

  return (
    <div className="App">
      <Header />

      <main className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-[#273e82] mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Voltar
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="bg-white rounded-xl overflow-hidden border border-slate-200 mb-4">
                <div className="aspect-square relative bg-slate-100">
                  <img
                    src={images[currentImageIndex]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    data-testid="product-main-image"
                  />

                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        data-testid="prev-image-button"
                      >
                        <ChevronLeft size={24} style={{ color: '#273e82' }} />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        data-testid="next-image-button"
                      >
                        <ChevronRight size={24} style={{ color: '#273e82' }} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-[#fef000]'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              {category && (
                <span
                  className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-4"
                  style={{ backgroundColor: '#F1F5F9', color: '#273e82' }}
                >
                  {category.name}
                </span>
              )}

              <h1
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: '#273e82', fontFamily: 'Manrope' }}
                data-testid="product-title"
              >
                {product.name}
              </h1>

              {product.reference && (
                <p className="text-base text-slate-500 mb-4" data-testid="product-reference">
                  Referência: {product.reference}
                </p>
              )}

              <div className="flex items-baseline gap-3 mb-6 flex-wrap">
                {hidePrice ? (
                  <span
                    className="text-xl md:text-2xl font-bold"
                    style={{ color: '#273e82' }}
                    data-testid="product-price-hidden"
                  >
                    Adicione ao carrinho e consulte o preço
                  </span>
                ) : (
                  <>
                    {hasDiscount && (
                      <span className="text-2xl text-slate-400 line-through" data-testid="original-price">
                        R$ {Number(product.price).toFixed(2)}
                      </span>
                    )}
                    <span
                      className="text-4xl font-bold"
                      style={{ color: '#273e82' }}
                      data-testid="product-price"
                    >
                      R$ {Number(displayPrice).toFixed(2)}
                    </span>
                    {hasDiscount && (
                      <span
                        className="px-3 py-1 rounded-full text-sm font-bold"
                        style={{ backgroundColor: '#fef000', color: '#273e82' }}
                      >
                        {Math.round(((product.price - product.promo_price) / product.price) * 100)}% OFF
                      </span>
                    )}
                  </>
                )}
              </div>

              {product.description && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold mb-3" style={{ color: '#273e82' }}>
                    Descrição
                  </h3>
                  <p className="text-slate-700 leading-relaxed" data-testid="product-description">
                    {product.description}
                  </p>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                  Quantidade
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-lg border-2 border-slate-300 hover:border-[#273e82] font-bold text-lg transition-colors"
                    data-testid="decrease-quantity"
                  >
                    -
                  </button>
                  <span className="text-xl font-bold min-w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-lg border-2 border-slate-300 hover:border-[#273e82] font-bold text-lg transition-colors"
                    data-testid="increase-quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleBuyNow}
                  className="flex-1 primary-button"
                  data-testid="buy-now-button"
                >
                  Comprar Agora
                </button>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 secondary-button flex items-center justify-center gap-2"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart size={20} />
                  Adicionar ao Carrinho
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductPage;